import { useRef, useState } from "react";
import { Move, ZoomIn, ZoomOut, RotateCcw, Crop } from "lucide-react";
import { resolveUrl } from "@/lib/media";
import { frameStyle } from "@/lib/frame";

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

// Fallback preview shapes (used when format = auto / unset).
const ASPECT_CLS = {
  video: "aspect-video",
  landscape: "aspect-video",
  portrait: "aspect-[2/3] max-w-[200px]",
  square: "aspect-square max-w-[240px]",
  wide: "aspect-[21/9]",
};
// Preview shape driven by the chosen FORMAT (matches the public site container).
const FORMAT_PREVIEW = {
  horizontal: "aspect-video",
  vertical: "aspect-[2/3] max-w-[190px]",
  square: "aspect-square max-w-[230px]",
};

const FORMATS = [
  { v: "auto", label: "Auto" },
  { v: "original", label: "Proportions d'origine" },
  { v: "horizontal", label: "Horizontal" },
  { v: "vertical", label: "Vertical" },
  { v: "square", label: "Carré" },
];

const FITS = [
  { v: "cover", label: "Remplir le cadre", hint: "peut recadrer" },
  { v: "contain", label: "Proportions conservées", hint: "jamais recadrée ni déformée" },
];
const MAXWS = [
  { v: "full", label: "Pleine" },
  { v: "large", label: "Large" },
  { v: "medium", label: "Moyenne" },
];
const MINI_BTN = (active) =>
  `px-2.5 py-1 rounded-full border text-[10px] transition-colors ${active ? "border-electric text-electric bg-electric/10" : "border-white/15 text-white/60 hover:text-white hover:border-white/30"}`;

// Reusable GLOBAL image framing tool ("Mode Transformer"). Drag (mouse + touch),
// precise X/Y sliders, independent zoom, format of the frame, and a live preview
// that matches EXACTLY what the public site renders (same frameStyle). CSS only —
// the original file is never modified. Each image stores its own X / Y / Zoom / fit / format.
export const FrameEditor = ({ label = "Cadrage de l'image", src, value = {}, onChange, aspect = "video", testid = "frame-editor", hint, withFormat = true }) => {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const drag = useRef(null);
  const f = value || {};
  const zoom = f.zoom ?? 1;
  const posX = f.posX ?? 50;
  const posY = f.posY ?? 50;
  const fit = f.fit || "cover";
  const fmt = f.format || "auto";
  const set = (p) => onChange({ ...f, ...p });

  const startDrag = (e) => {
    if (!boxRef.current) return;
    boxRef.current.setPointerCapture?.(e.pointerId);
    const rect = boxRef.current.getBoundingClientRect();
    drag.current = { x: e.clientX, y: e.clientY, w: rect.width, h: rect.height, px: posX, py: posY };
  };
  const moveDrag = (e) => {
    if (!drag.current) return;
    const d = drag.current;
    // 1:1 grab feel: a full drag across the preview spans the whole 0–100 range.
    const dxPct = ((e.clientX - d.x) / d.w) * 100;
    const dyPct = ((e.clientY - d.y) / d.h) * 100;
    // Independent axes: X never touches Y and vice-versa.
    set({ posX: clamp(Math.round(d.px - dxPct), 0, 100), posY: clamp(Math.round(d.py - dyPct), 0, 100) });
  };
  const endDrag = () => { drag.current = null; };
  const bump = (d) => set({ zoom: clamp(Math.round((zoom + d) * 100) / 100, 1, 4) });

  const aspCls = FORMAT_PREVIEW[fmt] || ASPECT_CLS[aspect] || ASPECT_CLS.video;

  return (
    <div className="rounded-lg border border-white/10 bg-[#080808] p-3 space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2 inline-flex items-center gap-1.5"><Crop size={12} /> {label}</span>
        <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`${testid}-toggle`}
          className="inline-flex items-center gap-1.5 text-[11px] rounded-full border border-electric/30 text-electric px-3 py-1 hover:bg-electric hover:text-black transition-colors">
          <Move size={12} /> {open ? "Fermer" : "Cadrer"}
        </button>
      </div>
      {hint && open && <p className="text-[10px] text-secondary2/70">{hint}</p>}

      {open && (!src ? (
        <p className="text-[11px] text-secondary2 py-3 text-center">Ajoutez d'abord une image pour la cadrer.</p>
      ) : (
        <div className="flex flex-col md:flex-row gap-4 pt-1">
          <div className="w-full md:w-[240px] shrink-0">
            <div
              ref={boxRef}
              onPointerDown={startDrag}
              onPointerMove={moveDrag}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              data-testid={`${testid}-preview`}
              className={`relative ${aspCls} mx-auto rounded-md overflow-hidden border border-white/15 bg-[#0e0e0e] cursor-grab active:cursor-grabbing touch-none select-none`}
            >
              <img src={resolveUrl(src)} alt="" draggable={false} className="w-full h-full pointer-events-none" style={frameStyle(value, fmt === "original" ? "contain" : fit)} />
              <div className="pointer-events-none absolute inset-0 border border-white/10" />
            </div>
            <p className="text-[10px] text-secondary2 mt-1.5 text-center inline-flex w-full justify-center items-center gap-1"><Move size={10} /> Glissez l'image (souris ou doigt)</p>
          </div>

          <div className="flex-1 space-y-2.5">
            {withFormat && (
            <div className="space-y-1">
              <span className="text-[10px] uppercase text-secondary2">Format du cadre</span>
              <div className="flex flex-wrap gap-1.5">
                {FORMATS.map((o) => (
                  <button key={o.v} type="button" onClick={() => set({ format: o.v })} data-testid={`${testid}-format-${o.v}`}
                    className={`text-[11px] rounded-full border px-3 py-1 transition-colors ${fmt === o.v ? "border-electric text-electric bg-electric/10" : "border-white/15 text-white/70 hover:border-electric hover:text-electric"}`}>{o.label}</button>
                ))}
              </div>
            </div>
            )}
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase text-secondary2 w-16">Zoom</span>
              <button type="button" onClick={() => bump(-0.1)} data-testid={`${testid}-zoom-out`} className="p-1.5 rounded border border-white/10 text-white/70 hover:border-electric hover:text-electric"><ZoomOut size={13} /></button>
              <input type="range" min={1} max={4} step={0.02} value={zoom} onChange={(e) => set({ zoom: parseFloat(e.target.value) })} className="flex-1 accent-electric" data-testid={`${testid}-zoom`} />
              <button type="button" onClick={() => bump(0.1)} data-testid={`${testid}-zoom-in`} className="p-1.5 rounded border border-white/10 text-white/70 hover:border-electric hover:text-electric"><ZoomIn size={13} /></button>
              <span className="text-[11px] text-white/50 w-10 text-right">{Math.round(zoom * 100)}%</span>
            </div>
            <label className="flex items-center gap-2"><span className="text-[10px] uppercase text-secondary2 w-16">Position X</span>
              <input type="range" min={0} max={100} step={1} value={posX} onChange={(e) => set({ posX: parseInt(e.target.value, 10) })} className="flex-1 accent-electric" data-testid={`${testid}-posx`} />
              <span className="text-[11px] text-white/50 w-8 text-right">{posX}</span></label>
            <label className="flex items-center gap-2"><span className="text-[10px] uppercase text-secondary2 w-16">Position Y</span>
              <input type="range" min={0} max={100} step={1} value={posY} onChange={(e) => set({ posY: parseInt(e.target.value, 10) })} className="flex-1 accent-electric" data-testid={`${testid}-posy`} />
              <span className="text-[11px] text-white/50 w-8 text-right">{posY}</span></label>
            <div className="flex flex-wrap items-center gap-1.5 pt-1">
              <button type="button" onClick={() => set({ fit: "cover" })} data-testid={`${testid}-fill`} className={`text-[11px] rounded-full border px-3 py-1 transition-colors ${fit !== "contain" ? "border-electric text-electric" : "border-white/15 text-white/70 hover:border-electric hover:text-electric"}`}>Remplir</button>
              <button type="button" onClick={() => set({ fit: "contain" })} data-testid={`${testid}-fit-btn`} className={`text-[11px] rounded-full border px-3 py-1 transition-colors ${fit === "contain" ? "border-electric text-electric" : "border-white/15 text-white/70 hover:border-electric hover:text-electric"}`}>Ajuster</button>
              <button type="button" onClick={() => set({ posX: 50, posY: 50 })} data-testid={`${testid}-center`} className="text-[11px] rounded-full border border-white/15 text-white/70 px-3 py-1 hover:border-electric hover:text-electric transition-colors">Centrer</button>
              <button type="button" onClick={() => { const { posX, posY, zoom, fit, format, ...rest } = f; onChange(fmt !== "auto" ? { ...rest, format: fmt } : rest); }} data-testid={`${testid}-reset`} className="inline-flex items-center gap-1 text-[11px] rounded-full border border-white/15 text-white/70 px-3 py-1 hover:border-electric hover:text-electric transition-colors"><RotateCcw size={11} /> Réinitialiser</button>
            </div>
            {withFormat && (
              <div className="border-t border-white/10 pt-2 space-y-2" data-testid={`${testid}-block-opts`}>
                <span className="text-[10px] uppercase text-secondary2">Bloc d'affichage (ce média uniquement)</span>
                <div className="flex flex-wrap gap-1.5">
                  <button type="button" onClick={() => set({ noBg: f.noBg ? undefined : true })} data-testid={`${testid}-bg`} className={`text-[11px] rounded-full border px-3 py-1 transition-colors ${f.noBg ? "border-white/15 text-white/60" : "border-electric text-electric bg-electric/10"}`}>{f.noBg ? "Fond : masqué" : "Fond : visible"}</button>
                  <button type="button" onClick={() => set({ border: f.border ? undefined : true })} data-testid={`${testid}-border`} className={`text-[11px] rounded-full border px-3 py-1 transition-colors ${f.border ? "border-electric text-electric bg-electric/10" : "border-white/15 text-white/60"}`}>{f.border ? "Bordure : visible" : "Bordure : masquée"}</button>
                  {MAXWS.map((o) => (
                    <button key={o.v} type="button" onClick={() => set({ maxW: o.v === "full" ? undefined : o.v })} data-testid={`${testid}-maxw-${o.v}`} className={`text-[11px] rounded-full border px-3 py-1 transition-colors ${(f.maxW || "full") === o.v ? "border-electric text-electric bg-electric/10" : "border-white/15 text-white/60"}`}>Largeur {o.label.toLowerCase()}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
